import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../../PageHeader/PageHeader'
import AppButton from '../../../components/AppButton/AppButton';
import AppInput from '../../../components/AppInput/AppInput';
import { useDispatch, useSelector } from 'react-redux';
import MultiSelect from '../../../components/AppSelect/MultiSelect';
import { addChild, updateChild, searchParent, searchUser } from '../../../constant/Constant';
import { HitApi } from '../../../Api/ApiHit';
import { getSelectedUnit } from '../../../storage/Storage';
import FileuploadComp from '../../../components/SingleFileUpload/FileuploadComp';
import ServiceSteps from './ServiceSteps/ServiceSteps';
import { ChildValidationSchema } from '../../../validationscheema/ChildValidation';
import { setApiErrorJson } from '../../../redux/Actions/ApiAction';
import toast from 'react-hot-toast';
import { addIsDeliveredProductKey } from '../../../utils/utils';

function AddChild({ editMode = false }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const ApiReducer = useSelector((state) => state.ApiReducer);
  const [staffOptions, setStaffOptions] = useState([]);
  const [parent, setParent] = useState(null);
  const [steps, setSteps] = useState([]);
  const [stepError, setStepError] = useState('')
  const dispatch = useDispatch()
  const navigate = useNavigate();
  const { id: urlId, parentId } = useParams();

  // Determine parentId based on mode
  const parentUrlId = editMode ? ApiReducer?.apiJson?.parentId?._id : (parentId || urlId);

  useEffect(() => {
    if (parentUrlId) {
      getParent();
    }
    fetchStaff();
  }, [parentUrlId]);

  // Populate edit data when in edit mode
  useEffect(() => {
    if (editMode && ApiReducer?.apiJson && Object.keys(ApiReducer.apiJson).length > 0) {
      // Delay population to ensure all options are loaded
      const timer = setTimeout(() => {
        populateEditData();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [editMode, ApiReducer?.apiJson]);

  const populateEditData = () => {
    const editData = ApiReducer?.apiJson;
    if (!editData) return;

    // Set parent from the nested parentId object
    if (editData.parentId && typeof editData.parentId === 'object') {
      setParent(editData.parentId);
    }

    // Populate steps with unique IDs for React management
    if (editData.steps && editData.steps.length > 0) {
      const populatedSteps = editData.steps.map((step, index) => ({
        id: `step_${index}_${Date.now()}`,
        step: step.step,
        stepName: step.stepName,
        service_time: step.service_time,
        products: step.products.map((product, productIndex) => {
          // Handle the different data structure from backend
          let productId = "";
          let brand = "";
          let productType = "";
          let quantity = product.quantity || "";

          // Check if product is an object (from backend) or string (from frontend)
          if (typeof product.product === 'object' && product.product?._id) {
            // Backend structure - product is an object
            productId = product.product._id;
            brand = product.product.brand || product.brand || "";
            productType = product.product.productType || "";
          } else {
            // Frontend structure - product is an ID string
            productId = product.product || "";
            brand = product.brand || "";
            productType = product.productType || "";
          }

          return {
            id: `product_${index}_${productIndex}_${Date.now()}`,
            product: productId,
            brand: brand,
            productType: productType,
            quantity: quantity
          };
        })
      }));

      setSteps(populatedSteps);
    }
  };

  // Calculate total service time in minutes from all steps
  const calculateTotalServiceTime = () => {
    const totalMinutes = steps.reduce((total, step) => {
      const stepTime = parseInt(step.service_time) || 0;
      return total + stepTime;
    }, 0);
    return totalMinutes;
  };

  // Update total service time whenever steps change
  useEffect(() => {
    const totalTime = calculateTotalServiceTime();
  }, [steps]);

  // Validate existing steps before adding a new one
  const validateStepsBeforeAdd = () => {
    // Clear previous error
    setStepError('');

    // Check if there are any existing steps
    if (steps.length === 0) {
      return true; // No steps to validate, can add first step
    }

    // Check each existing step
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];

      // Check if step name is empty
      if (!step.stepName || step.stepName.trim() === '') {
        setStepError(`Step ${i + 1}: Step Name is required`);
        return false;
      }

      // Check if service time is empty
      if (!step.service_time || step.service_time.toString().trim() === '') {
        setStepError(`Step ${i + 1}: Service Time is required`);
        return false;
      }

      // Check if service time is a valid number
      if (isNaN(parseInt(step.service_time)) || parseInt(step.service_time) <= 0) {
        setStepError(`Step ${i + 1}: Service Time must be a valid number greater than 0`);
        return false;
      }
    }

    return true; // All validations passed
  };

  const addStep = () => {
    // Validate existing steps before adding new one
    if (!validateStepsBeforeAdd()) {
      return; // Don't add step if validation fails
    }

    const newStep = {
      id: Date.now().toString(),
      step: steps.length + 1,
      stepName: "",
      service_time: "",
      products: []
    };
    setSteps([...steps, newStep]);

    // Clear error since validation passed
    setStepError('');
  };

  const removeStep = (stepId) => {
    const updated = steps.filter((step) => step.id !== stepId);
    const reNumbered = updated.map((step, index) => ({
      ...step,
      step: index + 1
    }));
    setSteps(reNumbered);

    // Clear error when step is removed
    setStepError('');
  };

  const updateStep = (stepId, field, value) => {
    const updated = steps.map((step) =>
      step.id === stepId ? { ...step, [field]: value } : step
    );
    setSteps(updated);

    // Clear error when step is updated (user is fixing the issue)
    if (stepError && (field === 'stepName' || field === 'service_time')) {
      setStepError('');
    }
  };

  const addProductToStep = (stepId) => {
    const updated = steps.map((step) => {
      if (step.id === stepId) {
        const newProduct = {
          id: Date.now().toString() + "_prod",
          product: "",
          brand: "",
          productType: "",
          quantity: ""
        };
        return { ...step, products: [...step.products, newProduct] };
      }
      return step;
    });
    setSteps(updated);
  };

  const removeProductFromStep = (stepId, productId) => {
    const updated = steps.map((step) => {
      if (step.id === stepId) {
        const newProducts = step.products.filter(p => p.id !== productId);
        return { ...step, products: newProducts };
      }
      return step;
    });
    setSteps(updated);
  };

  const updateProduct = (stepId, productId, field, value) => {
    const updatedSteps = steps.map((step) => {
      if (step.id === stepId) {
        const updatedProducts = step.products.map((p) => {
          if (p.id === productId) {
            const updated = { ...p, [field]: value };

            if (field === 'brand' || field === 'productType') {
              updated.product = "";
            }

            return updated;
          }
          return p;
        });
        return { ...step, products: updatedProducts };
      }
      return step;
    });

    setSteps(updatedSteps);
  };

  const getParent = () => {
    setIsLoadingData(true);
    let json = {
      page: 1,
      limit: 1,
      search: {
        _id: parentUrlId,
         unitIds: getSelectedUnit()?._id
      }
    }
    HitApi(json, searchParent).then((res) => {
      if (res?.data?.docs?.[0]) {
        setParent(res.data?.docs?.[0]);
      } else {
        toast.error('Parent service not found');
        navigate('/services');
      }
    }).catch((error) => {
      console.error("Error fetching parent:", error);
      toast.error('Error loading parent service. Please try again.');
      navigate('/services');
    }).finally(() => {
      setIsLoadingData(false);
    });
  }

  const handleSubmit = () => {
    setIsLoading(true);

    // Validate all steps before submission
    if (!validateStepsBeforeAdd()) {
      setIsLoading(false);
      return;
    }

    const totalServiceTime = calculateTotalServiceTime();

    const cleanedSteps = steps.map((step, index) => {
      const allProducts = step.products.map((p) => ({
        product: p.product || "",
        brand: p.brand || "",
        productType: p.productType || "",
        quantity: p.quantity || ""
      }));

      return {
        stepName: step.stepName,
        step: index + 1,
        service_time: step.service_time,
        products: allProducts
      };
    });

    const validationPayload = {
      ...ApiReducer?.apiJson,
      parentId: parentUrlId,
      total_service_time: totalServiceTime,
      steps: cleanedSteps
    };

    ChildValidationSchema(validationPayload).then((error) => {
      dispatch(setApiErrorJson(error));

      if (Object.keys(error).length === 0) {
        if (editMode) {
          const updatePayload = {
            ...validationPayload
          };

          const {
            parentId: parentIdObj,
            staffDetails,
            unitDetails,
            ...cleanPayload
          } = updatePayload;

          cleanPayload._id = urlId;
          cleanPayload.parentId = parentUrlId;

          HitApi(cleanPayload, updateChild).then((res) => {
            if (res?.statusCode === 200) {
              toast.success('Service updated successfully!');
              navigate('/services');
            } else {
              toast.error(res?.message || 'Failed to update service');
            }
            setIsLoading(false);
          }).catch((error) => {
            console.error("Update error:", error);
            toast.error(`Update failed: ${error.message || 'Unknown error occurred'}`);
            setIsLoading(false);
          });
        } else {
          const createPayload = {
            ...validationPayload,
            unitIds: getSelectedUnit()?._id
          };
          var finalPayload = addIsDeliveredProductKey(createPayload)
          HitApi(finalPayload, addChild).then((res) => {
            if (res?.statusCode === 201) {
              toast.success('Service created successfully!');
              navigate('/services');
            } else {
              toast.error(res?.message || 'Failed to create service');
            }
            setIsLoading(false);
          }).catch((error) => {
            console.error("Create error:", error);
            toast.error('Error creating service. Please try again.');
            setIsLoading(false);
          });
        }
      } else {
        setIsLoading(false);
      }
    }).catch((validationError) => {
      console.error("Validation error:", validationError);
      toast.error('Validation failed. Please check your inputs.');
      setIsLoading(false);
    });
  };

  const fetchStaff = () => {
    var json = {
      page: 1,
      limit: 20,
      search: {
        userType: "Staff",
        unitIds: getSelectedUnit()?._id
      }
    };

    HitApi(json, searchUser).then((res) => {
      if (res?.statusCode === 200) {
        const options = res.data.docs?.map(staff => ({
          value: staff._id,
          label: `${staff.name}`,
        }));
        setStaffOptions(options);
      }
    }).catch((error) => {
      console.error("Error fetching staff:", error);
      toast.error('Failed to load staff options');
    });
  };

  const hasError = (fieldName) => {
    return ApiReducer.apiJsonError && ApiReducer.apiJsonError[fieldName];
  };

  // Helper function to get error message
  const getErrorMsg = (fieldName) => {
    return ApiReducer.apiJsonError ? ApiReducer.apiJsonError[fieldName] : "";
  };

  if (isLoadingData) {
    return (
      <div className='p-5'>
        <PageHeader
          title={editMode ? 'Update Service' : 'Add New Service'}
          description={editMode ? 'Update service information' : 'Create a new service'}
        />
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading parent information...</div>
        </div>
      </div>
    );
  }

  return (
    <div className='p-5'>
      <PageHeader
        title={editMode ? 'Update Service' : 'Add New Service'}
        description={editMode ? 'Update service information' : 'Create a new service'}
      />
      {parent ? (
        <>
          <div className="bg-white p-8 border rounded-xl mb-6">
            <div className="text-lg font-medium mb-5 flex justify-between items-center">
              <div className='font-bold'>{`Group : ${parent?.name}`}</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <AppInput
                title="Service Name"
                placeholder="Enter service name"
                name="name"
                important={true}
                error={hasError('name')}
                errormsg={getErrorMsg('name')}
                disabled={isLoading}
                str
              />

              <AppInput
                title="Regular Price (₹)"
                placeholder="Enter price"
                name="price"
                type='number'
                error={hasError('price')}
                errormsg={getErrorMsg('price')}
                important={true}
                disabled={isLoading}
                num
              />

              <AppInput
                title="Member Price (₹)"
                placeholder="Enter member price"
                name="member_price"
                type='number'
                disabled={isLoading}
                num
              />
              <AppInput
                title="Enter Incentive (%)"
                placeholder="Enter incentive (%)"
                name="incentive"
                type='number'
                disabled={isLoading}
                num
                error={hasError('incentive')}
              />
              <div>
                <AppInput
                  title="Total Service Time (minutes)"
                  placeholder="Auto-calculated"
                  value={calculateTotalServiceTime()}
                  disabled={true}
                />
                <p className="text-xs text-gray-500 mt-1">
                  This is automatically calculated from all steps
                </p>
              </div>

              <MultiSelect
                title="Assign Staff"
                name="staffIds"
                options={staffOptions}
                placeholder="Select staff members"
                specification="Choose staff members who can perform this service"
                disabled={isLoading}
              />
            </div>
            <div className='mb-5'>
              <FileuploadComp
                title="Image URL"
                name="img"
                allowed={["img"]}
                disabled={isLoading}
              />
            </div>
          </div>

          <ServiceSteps
            steps={steps}
            isLoading={isLoading}
            addStep={addStep}
            removeStep={removeStep}
            updateStep={updateStep}
            addProductToStep={addProductToStep}
            removeProductFromStep={removeProductFromStep}
            updateProduct={updateProduct}
            editMode={editMode}
            editData={ApiReducer?.apiJson}
            stepError={stepError} // Pass the error to ServiceSteps component
          />
        </>
      ) : (
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Parent service not found</div>
        </div>
      )}

      <div className='flex mt-5 justify-end'>
        <AppButton
          buttontext={isLoading ? "Processing..." : (editMode ? "Update Service" : "Add Service")}
          onClick={handleSubmit}
          disabled={isLoading}
          isLoading={isLoading}
        />
      </div>
    </div>
  )
}

export default AddChild